import React, { useState, useEffect } from 'react';
import { NineMensMorrisBoard } from './Icons.js';

/**
 * Nine Men's Morris Game Component
 * 
 * Game Rules:
 * - Each player has 9 pieces
 * - Game has three phases:
 *   1. Placement: Players alternate placing pieces on empty positions
 *   2. Movement: Players move pieces to adjacent empty positions
 *   3. Flying: When a player has only 3 pieces left, they can move to any empty position
 * - Forming a "mill" (3 pieces in a row) allows removing an opponent's piece
 * - A player wins when opponent has only 2 pieces or cannot make a move
 */
const NineMensMorris = () => {
  // Board state: array of 24 positions (null, 1 for player 1, 2 for player 2)
  const [board, setBoard] = useState(Array(24).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [phase, setPhase] = useState('placement'); // 'placement', 'movement', 'removal'
  const [piecesToPlace, setPiecesToPlace] = useState({ 1: 9, 2: 9 });
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [winner, setWinner] = useState(null);
  const [millFormed, setMillFormed] = useState(false);

  // Define adjacency connections for each position
  const adjacencyList = {
    0: [1, 7],
    1: [0, 2, 9],
    2: [1, 3],
    3: [2, 4, 11],
    4: [3, 5],
    5: [4, 6, 13],
    6: [5, 7],
    7: [6, 0, 15],
    8: [9, 15],
    9: [8, 10, 1, 17],
    10: [9, 11],
    11: [10, 12, 3, 19],
    12: [11, 13],
    13: [12, 14, 5, 21],
    14: [13, 15],
    15: [14, 8, 7, 23],
    16: [17, 23],
    17: [16, 18, 9],
    18: [17, 19],
    19: [18, 20, 11],
    20: [19, 21],
    21: [20, 22, 13],
    22: [21, 23],
    23: [22, 16, 15]
  };

  // Define all possible mills (3 positions that form a line)
  const mills = [
    // Outer square
    [0, 1, 2], [2, 3, 4], [4, 5, 6], [6, 7, 0],
    // Middle square
    [8, 9, 10], [10, 11, 12], [12, 13, 14], [14, 15, 8],
    // Inner square
    [16, 17, 18], [18, 19, 20], [20, 21, 22], [22, 23, 16],
    // Vertical connections
    [1, 9, 17], [3, 11, 19], [5, 13, 21], [7, 15, 23]
  ];

  /**
   * Check if a position is part of a mill for the given player
   */
  const isPartOfMill = (position, player, currentBoard = board) => {
    return mills.some(mill => {
      return mill.includes(position) && 
             mill.every(pos => currentBoard[pos] === player);
    });
  };

  /**
   * Check if forming a mill after placing/moving to a position
   */
  const checkMillFormed = (position, player, currentBoard) => {
    return isPartOfMill(position, player, currentBoard);
  };

  /**
   * Get count of pieces for a player on the board
   */
  const getPieceCount = (player) => {
    return board.filter(p => p === player).length;
  };

  /**
   * Check if a player can make any valid move
   */
  const hasValidMoves = (player) => {
    // Check if any piece can move to an adjacent empty position
    for (let i = 0; i < 24; i++) {
      if (board[i] === player) {
        // If player has 3 pieces, they can fly anywhere
        if (getPieceCount(player) === 3) {
          if (board.some(p => p === null)) return true;
        } else {
          // Check adjacent positions
          const adjacent = adjacencyList[i];
          if (adjacent.some(adj => board[adj] === null)) return true;
        }
      }
    }
    return false;
  };

  /**
   * Check for win condition
   */
  useEffect(() => {
    if (phase === 'placement') return;

    const opponent = currentPlayer === 1 ? 2 : 1;
    const opponentPieceCount = getPieceCount(opponent);

    // Win if opponent has only 2 pieces
    if (opponentPieceCount < 3) {
      setWinner(currentPlayer);
      return;
    }

    // Win if opponent has no valid moves
    if (!hasValidMoves(opponent) && !millFormed) {
      setWinner(currentPlayer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, phase, board]);

  /**
   * Handle position click
   */
  const handlePositionClick = (position) => {
    if (winner) return;

    // Removal phase - remove opponent's piece
    if (millFormed) {
      const opponent = currentPlayer === 1 ? 2 : 1;
      if (board[position] === opponent) {
        // Cannot remove piece that's part of a mill unless all pieces are in mills
        const allInMills = board.every((p, idx) => {
          if (p !== opponent) return true;
          return isPartOfMill(idx, opponent);
        });

        if (!isPartOfMill(position, opponent) || allInMills) {
          const newBoard = [...board];
          newBoard[position] = null;
          setBoard(newBoard);
          setMillFormed(false);
          
          // Check if game should transition to movement phase
          if (piecesToPlace[1] === 0 && piecesToPlace[2] === 0) {
            setPhase('movement');
          }
          
          // After removing opponent's piece, switch to opponent's turn
          // (currentPlayer formed the mill, removed a piece, now it's opponent's turn)
          setCurrentPlayer(opponent);
          setValidMoves([]);
        }
      }
      return;
    }

    // Placement phase
    if (phase === 'placement') {
      if (board[position] === null && piecesToPlace[currentPlayer] > 0) {
        const newBoard = [...board];
        newBoard[position] = currentPlayer;
        setBoard(newBoard);

        const newPiecesToPlace = { ...piecesToPlace };
        newPiecesToPlace[currentPlayer]--;
        setPiecesToPlace(newPiecesToPlace);

        // Check if mill was formed
        if (checkMillFormed(position, currentPlayer, newBoard)) {
          setMillFormed(true);
        } else {
          // Switch player
          const nextPlayer = currentPlayer === 1 ? 2 : 1;
          setCurrentPlayer(nextPlayer);

          // Check if placement phase is over
          if (newPiecesToPlace[1] === 0 && newPiecesToPlace[2] === 0) {
            setPhase('movement');
          }
        }
      }
      return;
    }

    // Movement phase
    if (phase === 'movement') {
      // Select a piece
      if (board[position] === currentPlayer && selectedPiece === null) {
        setSelectedPiece(position);
        
        // Calculate valid moves
        const playerPieceCount = getPieceCount(currentPlayer);
        let valid = [];
        
        if (playerPieceCount === 3) {
          // Flying: can move to any empty position
          valid = board.map((p, idx) => p === null ? idx : null).filter(idx => idx !== null);
        } else {
          // Normal movement: can only move to adjacent empty positions
          valid = adjacencyList[position].filter(adj => board[adj] === null);
        }
        
        setValidMoves(valid);
      }
      // Move the selected piece
      else if (selectedPiece !== null && validMoves.includes(position)) {
        const newBoard = [...board];
        newBoard[selectedPiece] = null;
        newBoard[position] = currentPlayer;
        setBoard(newBoard);
        setSelectedPiece(null);
        setValidMoves([]);

        // Check if mill was formed
        if (checkMillFormed(position, currentPlayer, newBoard)) {
          setMillFormed(true);
        } else {
          // Switch player
          setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
        }
      }
      // Deselect if clicking on same piece
      else if (position === selectedPiece) {
        setSelectedPiece(null);
        setValidMoves([]);
      }
      // Select different piece
      else if (board[position] === currentPlayer) {
        setSelectedPiece(position);
        
        // Calculate valid moves for new piece
        const playerPieceCount = getPieceCount(currentPlayer);
        let valid = [];
        
        if (playerPieceCount === 3) {
          valid = board.map((p, idx) => p === null ? idx : null).filter(idx => idx !== null);
        } else {
          valid = adjacencyList[position].filter(adj => board[adj] === null);
        }
        
        setValidMoves(valid);
      }
    }
  };

  /**
   * Reset game
   */
  const resetGame = () => {
    setBoard(Array(24).fill(null));
    setCurrentPlayer(1);
    setPhase('placement');
    setPiecesToPlace({ 1: 9, 2: 9 });
    setSelectedPiece(null);
    setValidMoves([]);
    setWinner(null);
    setMillFormed(false);
  };

  /**
   * Get game status message
   */
  const getStatus = () => {
    const colour = currentPlayer === 1 ? 'Brancas' : 'Pretas';

    if (winner) {
      return `Vencedor: ${winner === 1 ? 'Brancas' : 'Pretas'}!`;
    }

    if (millFormed) {
      return `${colour}: remover peça adversária`;
    }

    if (phase === 'placement') {
      return `${colour}: colocar peça (${piecesToPlace[currentPlayer]} restantes)`;
    }

    if (phase === 'movement') {
      const canFly = getPieceCount(currentPlayer) === 3;
      return `${colour}: ${canFly ? 'mover peça em voo' : 'mover peça'}`;
    }

    return '';
  };

  return (
    <div className="game-container">
      <h1 className="game-title">Moinho</h1>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      <NineMensMorrisBoard
        board={board}
        onPositionClick={handlePositionClick}
        validMoves={validMoves}
      />
      <button className="new-game-button" onClick={resetGame}>
        Novo Jogo
      </button>
    </div>
  );
};

export default NineMensMorris;
